import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { createLeader } from "@/lib/api/leaders.functions";
import { MediaField } from "@/components/admin/MediaPicker";
import { SortableStringList } from "@/components/admin/SortableStringList";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import type { MediaSelection } from "@/lib/media/types";

export const Route = createFileRoute("/admin/leaders/new")({
  component: NewLeaderPage,
});

function NewLeaderPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [navLabel, setNavLabel] = useState("");
  const [designation, setDesignation] = useState("");
  const [qualifications, setQualifications] = useState("");
  const [initials, setInitials] = useState("");
  const [status, setStatus] = useState<"draft" | "published">("published");
  const [memberships, setMemberships] = useState<string[]>([""]);
  const [bio, setBio] = useState<string[]>([""]);
  const [portrait, setPortrait] = useState<MediaSelection>({});
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const row = await createLeader({
        data: {
          name,
          navLabel: navLabel || (name.split(" ")[0] ?? name),
          designation,
          qualifications,
          initials,
          status,
          memberships: memberships.filter(Boolean),
          bio: bio.filter(Boolean),
          portraitMediaId: portrait.mediaId,
          portraitUrl: portrait.url,
        },
      });
      toast.success("Leader created");
      navigate({ to: "/admin/leaders/$id", params: { id: row.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="New leader" />
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Nav label</Label>
            <Input value={navLabel} onChange={(e) => setNavLabel(e.target.value)} placeholder="Short name for navigation" />
          </div>
          <div className="space-y-2">
            <Label>Initials</Label>
            <Input value={initials} onChange={(e) => setInitials(e.target.value)} placeholder="AL" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Designation</Label>
          <Input value={designation} onChange={(e) => setDesignation(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Qualifications</Label>
          <Input value={qualifications} onChange={(e) => setQualifications(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <MediaField label="Portrait" value={portrait} onChange={setPortrait} />
        <SortableStringList label="Memberships & credentials" items={memberships} onChange={setMemberships} />
        <SortableStringList label="Biography paragraphs" items={bio} onChange={setBio} multiline />
      </div>
      <AdminFormFooter
        cancelTo="/admin/leaders"
        onSave={handleSave}
        saving={saving}
        saveLabel="Create leader"
        savingLabel="Creating…"
      />
    </AdminPageShell>
  );
}
